/* import __COLOCATED_TEMPLATE__ from './fin-readiness-table.hbs'; */
/* RESPONSIBLE TEAM: team-ai-insights */
import type AppService from 'embercom/services/app-service';
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';

import type Store from '@ember-data/store';
import type { ReportState } from 'embercom/components/reporting/custom/report/chart-card';
import { staticDataResource } from 'embercom/lib/reporting/static-data-resource';
import { use } from 'ember-resources/util/function-resource';
import type IntlService from 'embercom/services/intl';
import {
  filters,
  and,
} from 'embercom/components/reporting/bespoke/overview/conversation-funnel/series-config';

interface Args {
  reportState: ReportState;
  shouldShowPersonalized: boolean;
  selectedIndustryText: string;
  selectedIndustry: string;
}

const TEAMMATE_COST_PER_HOUR = 23;
const AVERAGE_TIME_TO_HANDLE_CONVERSATION = 35;
const FIN_RESOLUTION_COST = 0.99;

//hardcoded values for generic version. For more information, see this issue: https://github.com/intercom/intercom/issues/441095
interface IndustryTableData {
  informationalPercentage: string;
  amountSaved: number;
}

// hardcoded values for generic version of the page
// For more information, see this issue: https://github.com/intercom/intercom/issues/441095
const INDUSTRY_TABLE_DATA: Record<string, IndustryTableData> = {
  'unknown-industry': {
    informationalPercentage: '39',
    amountSaved: 6176,
  },
  'software-technology': {
    informationalPercentage: '47',
    amountSaved: 4399,
  },
  'gaming-gambling': {
    informationalPercentage: '27',
    amountSaved: 10414,
  },
  ecommerce: {
    informationalPercentage: '34',
    amountSaved: 6499,
  },
  'financial-services': {
    informationalPercentage: '39',
    amountSaved: 7705,
  },
};

export default class FinReadinessTable extends Component<Args> {
  @service declare appService: AppService;
  @service declare store: Store;
  @service declare intl: IntlService;

  @use informationalConversationsResource = staticDataResource(this, {
    serializedChart: () => this.informationalConversationsChartDefinition,
    reportState: () => this.args.reportState,
  });

  @use convWithQueryTypeResource = staticDataResource(this, {
    serializedChart: () => this.convWithQueryTypeChartDefinition,
    reportState: () => this.args.reportState,
  });

  @use informationalTeammateHandledResource = staticDataResource(this, {
    serializedChart: () => this.informationalTeammateHandledChartDefinition,
    reportState: () => this.args.reportState,
  });

  get informationalConversationsFilter() {
    return {
      type: 'category',
      data: {
        property: 'conversation_custom_fields#ai_insights_query_type',
        attribute: 'conversation_custom_fields#ai_insights_query_type',
        values: ['informational'],
      },
    };
  }

  get queryTypeFilter() {
    return {
      type: 'exists',
      data: {
        property: 'conversation_custom_fields#ai_insights_query_type',
        attribute: 'conversation_custom_fields#ai_insights_query_type',
      },
    };
  }

  get informationalTeammateHandledFilter() {
    return {
      type: 'and',
      filters: [
        and(
          this.informationalConversationsFilter,
          filters.teammateWasEverInvolved,
          filters.finWasNeverInvolved,
        ),
      ],
    };
  }

  get informationalConversationsChartDefinition() {
    return {
      visualization_type: 'counter',
      view_by: null,
      chart_series: [
        {
          metric_id: 'v1.new_conversations',
          filters: {
            type: 'and',
            filters: [and(this.informationalConversationsFilter, filters.finWasNeverInvolved)],
          },
        },
      ],
    };
  }

  get convWithQueryTypeChartDefinition() {
    return {
      visualization_type: 'counter',
      view_by: null,
      chart_series: [
        {
          metric_id: 'v1.new_conversations',
          filters: {
            type: 'and',
            filters: [and(this.queryTypeFilter, filters.finWasNeverInvolved)],
          },
        },
      ],
    };
  }

  get informationalTeammateHandledChartDefinition() {
    return {
      visualization_type: 'counter',
      view_by: null,
      chart_series: [
        {
          metric_id: 'v1.new_conversations',
          filters: this.informationalTeammateHandledFilter,
        },
      ],
    };
  }

  get informationalCount() {
    if (
      !this.informationalConversationsResource.kpiData ||
      !this.informationalConversationsResource.kpiData[0]
    ) {
      return 0;
    }
    return this.informationalConversationsResource.kpiData[0] as number;
  }

  get totalConversations() {
    if (!this.convWithQueryTypeResource.kpiData || !this.convWithQueryTypeResource.kpiData[0]) {
      return 0;
    }
    return this.convWithQueryTypeResource.kpiData[0] as number;
  }

  get informationalTeammateHandledCount() {
    if (
      !this.informationalTeammateHandledResource.kpiData ||
      !this.informationalTeammateHandledResource.kpiData[0]
    ) {
      return 0;
    }
    return this.informationalTeammateHandledResource.kpiData[0] as number;
  }

  getHardcodedDataForIndustry(industry: string): IndustryTableData {
    return INDUSTRY_TABLE_DATA[industry] || INDUSTRY_TABLE_DATA['unknown-industry'];
  }

  get informationalPercentage() {
    if (!this.args.shouldShowPersonalized) {
      let hardcodedData = this.getHardcodedDataForIndustry(this.args.selectedIndustry);
      return `${hardcodedData.informationalPercentage}%`;
    }
    if (this.totalConversations === 0) {
      return '0%';
    }
    return `${((this.informationalCount / this.totalConversations) * 100).toFixed(0)}%`;
  }

  get informationalDescription() {
    if (!this.args.shouldShowPersonalized) {
      let suffix = this.args.selectedIndustry === 'unknown-industry' ? 'general' : 'generic';
      return this.intl.t(
        `automation.ai-insights.fin-readiness.where-to-start.informational-queries.${suffix}-description`,
        {
          htmlSafe: true,
          selectedIndustry: this.args.selectedIndustryText,
        },
      );
    }
    return this.intl.t(
      'automation.ai-insights.fin-readiness.where-to-start.informational-queries.description',
      {
        count: this.intl.formatNumber(this.informationalCount),
        total: this.intl.formatNumber(this.totalConversations),
      },
    );
  }

  get amountSavedTooltip() {
    let tooltipKey = this.args.shouldShowPersonalized
      ? 'automation.ai-insights.fin-readiness.where-to-start.amount-saved.tooltip'
      : 'automation.ai-insights.fin-readiness.where-to-start.amount-saved.generic-tooltip';

    return this.intl.t(tooltipKey, {
      htmlSafe: true,
      url: 'https://www.intercom.com/pricing/fin-roi',
      averageTime: this.averageTime,
      teammateCost: this.teammateCost,
    });
  }

  get averageTime() {
    return AVERAGE_TIME_TO_HANDLE_CONVERSATION;
  }

  get teammateCost() {
    return this.intl.formatNumber(TEAMMATE_COST_PER_HOUR, {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0,
    });
  }

  get amountSavedUsingFin() {
    if (!this.args.shouldShowPersonalized) {
      let hardcodedData = this.getHardcodedDataForIndustry(this.args.selectedIndustry);
      return this.intl.formatNumber(hardcodedData.amountSaved, {
        style: 'currency',
        currency: 'USD',
        maximumFractionDigits: 0,
      });
    }
    let teammateCost =
      this.informationalTeammateHandledCount *
      (AVERAGE_TIME_TO_HANDLE_CONVERSATION / 60) *
      TEAMMATE_COST_PER_HOUR;
    let finResolutionCost = this.informationalTeammateHandledCount * FIN_RESOLUTION_COST;
    return this.intl.formatNumber(teammateCost - finResolutionCost, {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0,
    });
  }

  get amountSavedFontClass() {
    let amount = this.amountSavedUsingFin;
    let length = amount.length;

    if (length < 8) {
      return 'table-numbers-large';
    } else if (length < 9) {
      return 'table-numbers-medium';
    } else if (length < 12) {
      return 'table-numbers-small';
    } else {
      return 'table-numbers-tiny';
    }
  }

  get amountSavedDescription() {
    if (!this.args.shouldShowPersonalized) {
      return this.intl.t(
        'automation.ai-insights.fin-readiness.where-to-start.amount-saved.generic-description',
      );
    }
    return this.intl.t(
      'automation.ai-insights.fin-readiness.where-to-start.amount-saved.description',
    );
  }

  get isLoading() {
    return (
      this.informationalConversationsResource.isLoading ||
      this.convWithQueryTypeResource.isLoading ||
      this.informationalTeammateHandledResource.isLoading
    );
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Reporting::Automation::AiInsights::FinReadiness::FinReadinessTable': typeof FinReadinessTable;
  }
}
